import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Scale, AlertTriangle, FileCheck, Plus, CheckCircle2, Clock3, XCircle, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

const CONFLICT_STATUS = {
  pending: { label: "Pending review", cls: "bg-amber-500/15 text-amber-600", icon: Clock3 },
  cleared: { label: "Cleared", cls: "bg-emerald-500/15 text-emerald-600", icon: CheckCircle2 },
  conflict: { label: "Conflict found", cls: "bg-primary/15 text-primary", icon: XCircle },
};
const INCIDENT_STATUS = {
  open: { label: "Open", cls: "bg-primary/15 text-primary" },
  investigating: { label: "Investigating", cls: "bg-amber-500/15 text-amber-600" },
  closed: { label: "Closed", cls: "bg-emerald-500/15 text-emerald-600" },
};
const SEVERITY = { low: "text-muted-foreground", medium: "text-amber-600", high: "text-primary" };

const Chip = ({ cls, children }) => <span className={`text-[11px] px-2 py-0.5 rounded-sm font-medium ${cls}`}>{children}</span>;
const fmt = (t) => { try { return new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return t; } };

export default function Ethics() {
  const { user } = useAuth();
  const isManager = ["admin", "manager"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  return (
    <div data-testid="ethics-page">
      <PageHeader overline="Compliance" title="Ethics & Risk" subtitle="Conflict-check log, incident reporting, and firm policy acknowledgements." />
      <Tabs defaultValue="conflicts">
        <TabsList data-testid="ethics-tabs">
          <TabsTrigger value="conflicts" data-testid="tab-conflicts"><Scale className="h-4 w-4 mr-1.5" /> Conflict Checks</TabsTrigger>
          <TabsTrigger value="incidents" data-testid="tab-incidents"><AlertTriangle className="h-4 w-4 mr-1.5" /> Incidents</TabsTrigger>
          <TabsTrigger value="policies" data-testid="tab-policies"><FileCheck className="h-4 w-4 mr-1.5" /> Policies</TabsTrigger>
        </TabsList>
        <TabsContent value="conflicts" className="mt-6"><Conflicts isManager={isManager} /></TabsContent>
        <TabsContent value="incidents" className="mt-6"><Incidents isManager={isManager} /></TabsContent>
        <TabsContent value="policies" className="mt-6"><Policies isAdmin={isAdmin} isManager={isManager} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Conflicts({ isManager }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ party_a: "", party_b: "", matter: "", description: "" });
  const load = useCallback(() => api.get("/ethics/conflicts", { params: status !== "all" ? { status } : {} }).then((r) => setItems(r.data)).catch(() => {}), [status]);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!form.party_a.trim()) return toast.error("Client / party name required");
    try { await api.post("/ethics/conflicts", form); toast.success("Conflict check submitted"); setOpen(false); setForm({ party_a: "", party_b: "", matter: "", description: "" }); load(); }
    catch { toast.error("Could not submit"); }
  };
  const resolve = async (id, newStatus) => {
    try { await api.patch(`/ethics/conflicts/${id}`, { status: newStatus }); toast.success("Updated"); load(); } catch { toast.error("Could not update"); }
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48" data-testid="conflict-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="cleared">Cleared</SelectItem>
            <SelectItem value="conflict">Conflict found</SelectItem>
          </SelectContent>
        </Select>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="ml-auto" data-testid="conflict-new-btn"><Plus className="h-4 w-4 mr-1.5" /> New Conflict Check</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Run a conflict check</DialogTitle><DialogDescription>Submit the parties to screen against existing clients and matters.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Client / Prospective party *</Label><Input data-testid="conflict-party-a" value={form.party_a} onChange={(e) => setForm({ ...form, party_a: e.target.value })} /></div>
              <div><Label className="text-xs">Opposing / adverse parties</Label><Input data-testid="conflict-party-b" value={form.party_b} onChange={(e) => setForm({ ...form, party_b: e.target.value })} placeholder="Comma-separated" /></div>
              <div><Label className="text-xs">Matter / description</Label><Input data-testid="conflict-matter" value={form.matter} onChange={(e) => setForm({ ...form, matter: e.target.value })} /></div>
              <div><Label className="text-xs">Notes</Label><Textarea rows={4} data-testid="conflict-notes" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <DialogFooter><Button data-testid="conflict-submit" onClick={submit}>Submit</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {items.length === 0 ? <EmptyState icon={Scale} title="No conflict checks yet" hint="Submit one with the button above." /> : (
        <div className="space-y-2" data-testid="conflict-list">
          {items.map((c) => {
            const st = CONFLICT_STATUS[c.status] || CONFLICT_STATUS.pending; const Icon = st.icon;
            return (
              <div key={c.id} data-testid="conflict-item" className="border border-border bg-card rounded-sm p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">{c.party_a} {c.party_b && <span className="text-muted-foreground font-normal">v. {c.party_b}</span>}</div>
                    {c.matter && <div className="text-xs text-muted-foreground">{c.matter}</div>}
                    {c.description && <div className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{c.description}</div>}
                    <div className="text-[11px] text-muted-foreground mt-1.5">Requested by {c.requested_by} · {fmt(c.created_at)}{c.resolved_by && ` · resolved by ${c.resolved_by}`}</div>
                  </div>
                  <Chip cls={st.cls}><span className="inline-flex items-center gap-1"><Icon className="h-3 w-3" />{st.label}</span></Chip>
                </div>
                {isManager && c.status === "pending" && (
                  <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                    <Button size="sm" variant="outline" data-testid="conflict-clear" onClick={() => resolve(c.id, "cleared")}><CheckCircle2 className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Clear</Button>
                    <Button size="sm" variant="outline" data-testid="conflict-flag" onClick={() => resolve(c.id, "conflict")}><XCircle className="h-3.5 w-3.5 mr-1 text-primary" /> Flag conflict</Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Incidents({ isManager }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const BLANK = { type: "Security", severity: "medium", title: "", description: "", occurred_on: "", anonymous: false };
  const [form, setForm] = useState(BLANK);
  const load = useCallback(() => api.get("/ethics/incidents").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    try { await api.post("/ethics/incidents", form); toast.success("Incident reported"); setOpen(false); setForm(BLANK); load(); }
    catch { toast.error("Could not submit"); }
  };
  const setStatus = async (id, status) => { try { await api.patch(`/ethics/incidents/${id}`, { status }); load(); } catch { toast.error("Could not update"); } };
  return (
    <div>
      <div className="flex mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="ml-auto" data-testid="incident-new-btn"><Plus className="h-4 w-4 mr-1.5" /> Report Incident</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Report an incident</DialogTitle><DialogDescription>Security, ethics, or safety concerns. Managers review every report.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger data-testid="incident-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Security", "Ethics", "Safety", "Data / Privacy", "Other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">Severity</Label>
                  <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}><SelectTrigger data-testid="incident-severity"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent></Select></div>
              </div>
              <div><Label className="text-xs">Title *</Label><Input data-testid="incident-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label className="text-xs">What happened?</Label><Textarea rows={4} data-testid="incident-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div><Label className="text-xs">Date it occurred</Label><Input type="date" data-testid="incident-date" value={form.occurred_on} onChange={(e) => setForm({ ...form, occurred_on: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="incident-anon" checked={form.anonymous} onChange={(e) => setForm({ ...form, anonymous: e.target.checked })} /> Report anonymously</label>
            </div>
            <DialogFooter><Button data-testid="incident-submit" onClick={submit}>Submit report</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {items.length === 0 ? <EmptyState icon={AlertTriangle} title="No incidents reported" hint="Use the button above to report a concern." /> : (
        <div className="space-y-2" data-testid="incident-list">
          {items.map((it) => {
            const st = INCIDENT_STATUS[it.status] || INCIDENT_STATUS.open;
            return (
              <div key={it.id} data-testid="incident-item" className="border border-border bg-card rounded-sm p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><span className={`text-[10px] uppercase font-bold ${SEVERITY[it.severity]}`}>{it.severity}</span><span className="text-xs text-muted-foreground">{it.type}</span></div>
                    <div className="font-semibold text-sm mt-0.5">{it.title}</div>
                    {it.description && <div className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{it.description}</div>}
                    <div className="text-[11px] text-muted-foreground mt-1.5">Reported by {it.reporter}{it.occurred_on && ` · occurred ${it.occurred_on}`} · {fmt(it.created_at)}</div>
                  </div>
                  <Chip cls={st.cls}>{st.label}</Chip>
                </div>
                {isManager && (
                  <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                    {["open", "investigating", "closed"].map((s) => (
                      <Button key={s} size="sm" variant={it.status === s ? "default" : "outline"} data-testid={`incident-set-${s}`} onClick={() => setStatus(it.id, s)} className="capitalize">{s}</Button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Policies({ isAdmin, isManager }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", version: "1" });
  const load = useCallback(() => api.get("/ethics/policies").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    try { await api.post("/ethics/policies", form); toast.success("Policy published"); setOpen(false); setForm({ title: "", body: "", version: "1" }); load(); }
    catch { toast.error("Could not save"); }
  };
  const ack = async (id) => { try { await api.post(`/ethics/policies/${id}/ack`); toast.success("Acknowledged"); load(); } catch { toast.error("Could not acknowledge"); } };
  const remove = async (id) => { try { await api.delete(`/ethics/policies/${id}`); load(); toast.success("Deleted"); } catch { toast.error("Could not delete"); } };
  return (
    <div>
      {isAdmin && (
        <div className="flex mb-4">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="ml-auto" data-testid="policy-new-btn"><Plus className="h-4 w-4 mr-1.5" /> New Policy</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Publish a policy</DialogTitle><DialogDescription>Staff will be asked to acknowledge it.</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-4 gap-3">
                  <div className="col-span-3"><Label className="text-xs">Title *</Label><Input data-testid="policy-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                  <div><Label className="text-xs">Version</Label><Input data-testid="policy-version" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} /></div>
                </div>
                <div><Label className="text-xs">Policy text</Label><Textarea rows={8} data-testid="policy-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
              </div>
              <DialogFooter><Button data-testid="policy-save" onClick={save}>Publish</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
      {items.length === 0 ? <EmptyState icon={FileCheck} title="No policies published" hint={isAdmin ? "Publish one with the button above." : undefined} /> : (
        <div className="space-y-3" data-testid="policy-list">
          {items.map((p) => (
            <div key={p.id} data-testid="policy-item" className="border border-border bg-card rounded-sm p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{p.title} <span className="text-xs text-muted-foreground font-normal">v{p.version}</span></div>
                  {p.body && <div className="text-xs text-muted-foreground mt-1 whitespace-pre-line line-clamp-4">{p.body}</div>}
                  {isManager && typeof p.ack_count === "number" && <div className="text-[11px] text-muted-foreground mt-2 inline-flex items-center gap-1"><Users className="h-3 w-3" /> {p.ack_count} acknowledgement(s)</div>}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  {p.acknowledged ? <Chip cls="bg-emerald-500/15 text-emerald-600"><span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Acknowledged</span></Chip>
                    : <Button size="sm" data-testid="policy-ack" onClick={() => ack(p.id)}>Acknowledge</Button>}
                  {isAdmin && <button data-testid="policy-delete" onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
